import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import type { IntakeData } from "@/lib/types";
import { CheckCircle, Mail, Calendar, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Trans } from "@lingui/macro";

interface SuccessStepProps {
  data: IntakeData;
}

export default function SuccessStep({ data }: SuccessStepProps) {
  useEffect(() => {
    // Add confetti or celebration animation here if desired
    console.log("🎉 Intake form submitted successfully!");
  }, []);

  const handleGoToDashboard = () => {
    // Redirect to Jema Manager dashboard
    const managerUrl = import.meta.env.VITE_JEMA_MANAGER_URL || "https://manager.jemaservices.com";
    window.location.href = managerUrl;
  };

  const handleReturnToWebsite = () => {
    const websiteUrl = import.meta.env.VITE_JEMA_WEBSITE_URL || "https://jemaservices.com.co";
    window.location.href = websiteUrl;
  };

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      {/* Success hero — matches intake v2 typography + brand accents */}
      <div className="space-y-6 py-8 text-center sm:py-10">
        <div className="mb-2 flex justify-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#166B93] shadow-lg shadow-[#166B93]/25">
            <CheckCircle className="h-10 w-10 text-white" strokeWidth={2.25} />
          </div>
        </div>
        <div className="space-y-3">
          <h1 className="text-[clamp(22px,5vw,36px)] font-bold leading-tight text-gray-900">
            <Trans>Congratulations!</Trans>
          </h1>
          <p className="text-lg font-semibold text-[#166B93] sm:text-xl">
            <Trans>Your intake form has been submitted successfully</Trans>
          </p>
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-gray-500">
            <Trans>
              Thank you, <strong>{data.firstName}</strong>! We've received your information and our medical team will review it shortly.
            </Trans>
          </p>
        </div>
      </div>

      {/* Reference Information */}
      {data.referenceUid && (
        <Card className="border-2 border-[#CEF12F] bg-gradient-to-br from-[#CEF12F]/10 to-white shadow-lg">
          <CardContent className="p-4 sm:p-6 lg:p-8">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#166B93] to-[#166B93]/80 rounded-xl mb-2">
                <Calendar className="h-8 w-8 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">
                  <Trans>Your Reference Number</Trans>
                </p>
                <p className="text-3xl font-bold text-[#166B93] font-mono tracking-wider">
                  {data.referenceUid}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  <Trans>Please save this number for your records</Trans>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* What Happens Next */}
      <Card className="border-0 shadow-xl">
        <CardContent className="p-4 sm:p-6 lg:p-8 space-y-6">
          <h2 className="text-2xl font-bold text-gray-900 font-nunito text-center mb-6">
            <Trans>What happens next?</Trans>
          </h2>
          
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-[#166B93] to-[#166B93]/80 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">1</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1 font-nunito">
                  <Trans>Medical Team Review</Trans>
                </h3>
                <p className="text-gray-600 text-sm font-jakarta">
                  <Trans>Our expert medical team will carefully review your information and medical history within 24-48 hours.</Trans>
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-[#CEF12F] to-[#CEF12F]/80 rounded-full flex items-center justify-center">
                  <span className="text-gray-900 font-bold text-lg">2</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1 font-nunito">
                  <Trans>Consultation Scheduling</Trans>
                </h3>
                <p className="text-gray-600 text-sm font-jakarta">
                  <Trans>
                    We'll contact you at <strong>{data.email}</strong> or <strong>{data.phone}</strong> to schedule your initial consultation.
                  </Trans>
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF9142] to-[#FF9142]/80 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">3</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1 font-nunito">
                  <Trans>Personalized Treatment Plan</Trans>
                </h3>
                <p className="text-gray-600 text-sm font-jakarta">
                  <Trans>After your consultation, you'll receive a detailed treatment plan with cost estimates and travel arrangements.</Trans>
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Confirmation */}
      <Card className="border-0 bg-gradient-to-br from-[#166B93]/5 via-white to-[#CEF12F]/5 shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <Mail className="h-6 w-6 text-[#166B93] mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-semibold text-gray-800 font-nunito">
                <Trans>Check Your Email</Trans>
              </p>
              <p className="text-sm text-gray-600 font-jakarta leading-relaxed">
                <Trans>
                  We've sent a confirmation email to <strong>{data.email}</strong> with all the details of your submission. Please check your inbox (and spam folder) for next steps.
                </Trans>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 pt-6">
        <Button
          onClick={handleGoToDashboard}
          className="flex-1 h-14 px-8 text-lg bg-gradient-to-r from-[#166B93] to-[#166B93]/90 hover:from-[#166B93]/90 hover:to-[#166B93]/80 shadow-lg hover:shadow-xl transition-all duration-200 font-semibold"
        >
          <div className="flex items-center gap-2">
            <Trans>Go to Dashboard</Trans>
            <ArrowRight className="w-5 h-5" />
          </div>
        </Button>
        <Button
          variant="outline"
          onClick={handleReturnToWebsite}
          className="sm:w-auto h-14 px-8 text-lg border-2 hover:bg-gray-50"
        >
          <Trans>Return to Website</Trans>
        </Button>
      </div>

      {/* Support Information */}
      <div className="text-center text-sm text-gray-600 font-jakarta pt-8 border-t border-gray-200">
        <p>
          <Trans>Questions or need assistance?</Trans>{" "}
          <a href="mailto:support@jemaservices.com" className="text-[#166B93] hover:underline font-semibold">
            <Trans>Contact our support team</Trans>
          </a>
        </p>
      </div>
    </div>
  );
}

